import type { Require } from "types/utilTypes";

import { AccessDeniedView } from "components/common/AccessDeniedView";
import { Skeleton } from "components/ui/legacy/Skeleton";
import { SwitchTransitions } from "components/ui/SwitchTransitions";
import { useSideNavContext } from "context/sideNavContext";
import useAnimationResolver, { type TResolveMatch, type TResolveOrderMatch } from "hooks/useAnimationResolver";
import { useRouteAccess } from "hooks/useRouteAccess";
import { Suspense, useEffect, useMemo } from "react";
import { Route, useLocation } from "react-router";
import { type Route as IRoute } from "routes/route";
import { routes, type TRoutes } from "routes/routes";
import { flattenRoutes } from "utils/router/utils";

const basePathAdder = (basePath: string) => (path: string) => basePath + path;

const routeHasAnimationOrder = (route: IRoute): route is Require<IRoute, "animationOrder"> =>
  typeof route.animationOrder === "number";

const calculateRoutesCustomAnimations = (
  routes: TRoutes,
  resolveMatches: (TResolveMatch | TResolveOrderMatch)[] = [],
  basePath = "",
) => {
  Object.values(routes).forEach((route) => {
    const addBasePath = basePathAdder(basePath);

    const targets = Object.values(routes)
      .filter((possibleRoute) => possibleRoute !== route)
      .map(({ matchesWithChildren }) => matchesWithChildren)
      .flat()
      .map(addBasePath);

    if (route.inAnimation) {
      resolveMatches.push({
        animation: route.inAnimation.name,
        from: targets,
        reverse: route.inAnimation.reverse,
        to: route.matches.map(addBasePath),
      });
    }

    if (route.outAnimation) {
      resolveMatches.push({
        animation: route.outAnimation.name,
        from: route.matches.map(addBasePath),
        reverse: route.outAnimation.reverse,
        to: targets,
      });
    }

    if (route.subRoutes) {
      const { subRoutes } = route;
      route.matches.forEach((match) => {
        calculateRoutesCustomAnimations(subRoutes, resolveMatches, match);
      });

      const subRoutesTargets = Object.values(route.subRoutes)
        .filter((possibleRoute) => possibleRoute !== route)
        .map(({ matches }) => matches)
        .flat()
        .map((subRouteMatch) => route.matches.map(addBasePath).map((match) => match + subRouteMatch))
        .flat();

      resolveMatches.push({
        animation: "fade-move-from-center",
        from: subRoutesTargets,
        reverse: false,
        to: route.matches.map(addBasePath),
      });

      resolveMatches.push({
        animation: "fade-move-from-center",
        from: route.matches.map(addBasePath),
        reverse: true,
        to: subRoutesTargets,
      });

      resolveMatches.push({
        animation: "move-from-side",
        order: Object.values(route.subRoutes)
          .filter(routeHasAnimationOrder)
          .sort((a, b) => a.animationOrder - b.animationOrder)
          .map(({ matches: subRouteMatches }) =>
            route.matches.map((match) => subRouteMatches.map(basePathAdder(match))).flat(),
          ),
      });
    }
  });

  return resolveMatches;
};

const PageWrapper: FC<{ route: IRoute }> = ({ route }) => {
  const { hideSideNav, Page } = route;
  const {
    actions: { setHideSideNav },
    state: { hideSideNav: contextHideSideNav },
  } = useSideNavContext();
  const location = useLocation();

  // we need location here to trigger the use effect anytime the location changes
  // this is because the animations can cause the page not to rerender if switched back and forth fast enough
  useEffect(() => {
    setHideSideNav(hideSideNav);
  }, [setHideSideNav, hideSideNav, location]);

  if (contextHideSideNav !== hideSideNav) {
    return null;
  }

  return (
    <Suspense fallback={<Skeleton data-testid="page-loading" height="100%" width="100%" />}>
      <Page />
    </Suspense>
  );
};

const RouteElement: FC<{ route: IRoute }> = ({ route }) => {
  const { allowed, loading } = useRouteAccess(route);
  if (loading) return <Skeleton data-testid="page-loading" height="100%" width="100%" />;
  if (!allowed) return <AccessDeniedView />;
  return <PageWrapper route={route} />;
};

export function PageRouter() {
  const location = useLocation();

  const flatRoutes = useMemo(() => flattenRoutes({ routes }), []);

  const resolvedMatches: (TResolveMatch | TResolveOrderMatch)[] = useMemo(
    () => [
      ...calculateRoutesCustomAnimations(routes),
      {
        animation: "fade-move-from-bottom",
        order: flatRoutes
          .filter(routeHasAnimationOrder)
          .sort((a, b) => a.animationOrder - b.animationOrder)
          .map(({ matches }) => matches),
      },
      {
        animation: "fade",
      },
    ],
    [flatRoutes],
  );

  const { animation, reverse } = useAnimationResolver(resolvedMatches, { currentLocation: location });

  return (
    <SwitchTransitions animation={animation} reverse={reverse}>
      {flatRoutes.flatMap((route) =>
        route.matches.map((path) => (
          <Route element={<RouteElement route={route} />} key={route.main + path} path={path} />
        )),
      )}
    </SwitchTransitions>
  );
}
